import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Play, Pause, Square, RotateCw, AlertCircle, CheckCircle2,
  Database, Cloud, FileWarning, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';

/**
 * Storage Migration Card — Faz 4 (Şub 03, 2026)
 * --------------------------------------------------------------
 * Mevcut local/Mongo dosyalarını S3'e taşıyan job'ı yönetir:
 *  • Bekleyen kayıt sayısı (koleksiyon başına)
 *  • Başlat / Duraklat / Devam Et / İptal butonları
 *  • Canlı progress bar (2 saniyede bir polling)
 *  • Hatalı dosyalar listesi (genişlet/daralt)
 */
const COLL_LABELS = {
  documents: 'Dökümanlar (Loonbrief vb.)',
  vehicle_documents: 'Araç Dökümanları',
  vehicle_photos: 'Araç Fotoğrafları',
  vergunning_codes: 'Vergunning Dosyaları',
};

const STATUS_BADGES = {
  idle: { label: 'Boşta', cls: 'bg-slate-100 text-slate-600' },
  running: { label: 'Çalışıyor', cls: 'bg-sky-100 text-sky-700' },
  paused: { label: 'Duraklatıldı', cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Tamamlandı', cls: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'İptal Edildi', cls: 'bg-rose-100 text-rose-700' },
  error: { label: 'Hata', cls: 'bg-rose-100 text-rose-700' },
};

export default function StorageMigrationCard({ s3Enabled }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/storage/migration/status');
      setData(r.data?.data || null);
    } catch (e) {
      // sessiz fail — gizli polling
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Aktif çalışıyorken her 2sn polling
  useEffect(() => {
    const active = data?.status === 'running' || data?.status === 'paused';
    if (active) {
      pollRef.current = setInterval(reload, 2000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [data?.status, reload]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(msg);
      await reload();
    } catch (e) {
      toast.error('Hata: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Migration durumu yükleniyor…
        </CardContent>
      </Card>
    );
  }

  const status = data.status || 'idle';
  const badge = STATUS_BADGES[status] || STATUS_BADGES.idle;
  const pendingTotal = data.pending_total || 0;
  const total = data.total || 0;
  const processed = data.processed || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isActive = isRunning || isPaused;

  return (
    <Card data-testid="storage-migration-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" /> Mevcut Dosyaları S3&apos;e Taşı
          </CardTitle>
          <Badge className={badge.cls}>{badge.label}</Badge>
        </div>
        <p className="text-xs text-slate-500">
          Eski kayıtları (Mongo&apos;daki binary + local disk dosyaları) S3 bucket&apos;a kopyalar.
          Orijinal dosyalar <strong>silinmez</strong> — sadece yeni S3 etiketi alır.
          SHA-256 ile bütünlük doğrulanır.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(data.pending_by_collection || {}).map(([k, v]) => (
            <div key={k} className="border rounded p-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold truncate">{COLL_LABELS[k] || k}</div>
              <div className="text-xl font-bold tabular-nums">{v}</div>
              <div className="text-[10px] text-slate-400">bekliyor</div>
            </div>
          ))}
        </div>

        {/* Progress (active job) */}
        {isActive && (
          <div className="space-y-2 bg-sky-50 dark:bg-sky-950/30 p-3 rounded-lg border border-sky-100">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold flex items-center gap-1">
                {isRunning && <Loader2 className="h-3 w-3 animate-spin" />}
                {isPaused && <Pause className="h-3 w-3" />}
                {data.current_collection ? `Aktif: ${COLL_LABELS[data.current_collection] || data.current_collection}` : 'Hazırlanıyor…'}
              </span>
              <span className="font-mono tabular-nums">{processed} / {total} (%{pct})</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="grid grid-cols-3 gap-2 text-[10px]">
              <div className="text-emerald-600"><CheckCircle2 className="h-3 w-3 inline" /> Başarılı: <strong>{data.succeeded || 0}</strong></div>
              <div className="text-rose-600"><AlertCircle className="h-3 w-3 inline" /> Hatalı: <strong>{data.failed || 0}</strong></div>
              <div className="text-slate-500">Atlandı: <strong>{data.skipped || 0}</strong></div>
            </div>
          </div>
        )}

        {/* Completed/Cancelled summary */}
        {(status === 'completed' || status === 'cancelled' || status === 'error') && total > 0 && (
          <div className={`p-3 rounded-lg border ${
            status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
            status === 'error' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="text-xs font-semibold">
              {status === 'completed' && <><CheckCircle2 className="h-4 w-4 inline mr-1 text-emerald-600" /> Migration tamamlandı</>}
              {status === 'cancelled' && <><Square className="h-4 w-4 inline mr-1 text-amber-600" /> Migration iptal edildi</>}
              {status === 'error' && <><AlertCircle className="h-4 w-4 inline mr-1 text-rose-600" /> Migration hatayla bitti</>}
            </div>
            <div className="text-[11px] mt-1 text-slate-600">
              {processed} işlendi · {data.succeeded || 0} başarılı · {data.failed || 0} hatalı
            </div>
            {data.error_message && (
              <div className="text-xs text-rose-700 mt-2 font-mono">{data.error_message}</div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {!isActive && pendingTotal > 0 && (
            <Button onClick={() => act('/storage/migration/start', 'Migration başlatıldı')}
                    disabled={busy || !s3Enabled} data-testid="migration-start-btn">
              <Play className="h-4 w-4 mr-1" /> Başlat ({pendingTotal} dosya)
            </Button>
          )}
          {isRunning && (
            <Button variant="outline" onClick={() => act('/storage/migration/pause', 'Duraklatıldı')} disabled={busy}>
              <Pause className="h-4 w-4 mr-1" /> Duraklat
            </Button>
          )}
          {isPaused && (
            <Button onClick={() => act('/storage/migration/resume', 'Devam edildi')} disabled={busy}>
              <Play className="h-4 w-4 mr-1" /> Devam Et
            </Button>
          )}
          {isActive && (
            <Button variant="outline" onClick={() => act('/storage/migration/cancel', 'İptal edildi')}
                    disabled={busy} className="text-rose-600">
              <Square className="h-4 w-4 mr-1" /> İptal
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={reload} disabled={busy}>
            <RotateCw className="h-4 w-4 mr-1" /> Yenile
          </Button>
          {!s3Enabled && pendingTotal > 0 && (
            <span className="text-xs text-amber-600 self-center">
              <AlertCircle className="h-3 w-3 inline mr-1" /> S3 toggle&apos;ı kapalı — önce açın
            </span>
          )}
          {pendingTotal === 0 && !isActive && (
            <span className="text-xs text-emerald-600 self-center">
              <CheckCircle2 className="h-3 w-3 inline mr-1" /> Taşınacak dosya kalmadı
            </span>
          )}
        </div>

        {/* Errors */}
        {Array.isArray(data.errors) && data.errors.length > 0 && (
          <div className="border-t pt-3">
            <button onClick={() => setErrorsOpen(!errorsOpen)}
                    className="text-xs font-semibold text-rose-600 flex items-center gap-1 hover:underline">
              <FileWarning className="h-3 w-3" /> Hatalı Dosyalar ({data.errors.length})
              {errorsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {errorsOpen && (
              <div className="mt-2 max-h-48 overflow-y-auto text-[11px] space-y-1 font-mono bg-slate-50 dark:bg-slate-900 rounded p-2">
                {data.errors.slice().reverse().map((e, i) => (
                  <div key={i} className="border-b border-slate-200 dark:border-slate-700 py-1 last:border-0">
                    <span className="text-slate-400">[{e.collection}]</span>{' '}
                    <span className="text-slate-600">{e.doc_id?.slice(0, 8)}</span>:{' '}
                    <span className="text-rose-600">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
